import { useCallback, useEffect, useState } from "react";
import { UserButton, useUser } from "@clerk/clerk-react";
import { CheckCircle, Clock, ChatCircleDots, Lifebuoy, ArrowLeft } from "@phosphor-icons/react";
import { useApi } from "@/api/useApi";
import CommentsThread from "@/components/CommentsThread";
import { hasUnread } from "@/lib/commentReads";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { AskShee } from "@/components/AskShee";

const platformColors = { facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2" };

export default function StaffPortal() {
  const api = useApi();
  const { user } = useUser();
  const [workspaceName, setWorkspaceName] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, p] = await Promise.all([
        api.get("/workspace"),
        api.get("/posts?status=pending_review"),
      ]);
      setWorkspaceName(w.data?.name || null);
      setPosts(p.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#f5f5f5]" data-testid="staff-portal">
      <header className="sticky top-0 z-10 bg-white border-b border-[#e5e7eb] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-[8px] bg-[#5FA8B4] flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ fontFamily: "Montserrat" }}>A</div>
          <h1 className="text-[#3C4048] font-bold text-base sm:text-lg truncate" style={{ fontFamily: "Montserrat", fontWeight: 800 }} data-testid="portal-title">
            {workspaceName ? `${workspaceName} Staff Portal` : "Staff Portal"}
          </h1>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="portal-help-toggle" onClick={() => setShowHelp((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border border-[#e5e7eb] text-[#3A7A8C] hover:bg-[#f0f7f8] transition-colors">
            {showHelp ? <><ArrowLeft size={15} /> Back to review</> : <><Lifebuoy size={15} weight="fill" /> Help</>}
          </button>
          <UserButton afterSignOutUrl="/app" />
        </div>
      </header>

      {showHelp ? (
        <main className="max-w-xl mx-auto px-4 py-6 space-y-6" data-testid="portal-help">
          <div>
            <h2 className="text-[#3C4048] text-lg font-bold" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Getting started</h2>
            <p className="text-[#7A9090] text-sm">A quick guide to leaving feedback on upcoming posts.</p>
          </div>
          <OnboardingWizard role="org:member" embedded />
          <div>
            <h2 className="text-[#3C4048] text-lg font-bold mb-1" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Ask Shee</h2>
            <p className="text-[#7A9090] text-sm mb-3">Have a question? Shee can explain anything about your portal.</p>
            <AskShee compact />
          </div>
        </main>
      ) : (
      <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h2 className="text-[#3C4048] text-lg font-bold" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Hi {user?.firstName || "there"} 👋</h2>
          <p className="text-[#7A9090] text-sm">Upcoming posts — leave feedback or suggestions below. The Admin makes the final call on approving and publishing.</p>
        </div>

        {loading ? (
          <p className="text-[#7A9090] text-center py-10">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-10 text-center" data-testid="portal-empty">
            <CheckCircle size={40} weight="duotone" className="text-[#22c55e] mx-auto mb-3" />
            <p className="text-[#7A9090]">Nothing awaiting feedback right now.</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} data-testid={`portal-card-${post.id}`} className="bg-white border border-[#e5e7eb] rounded-[12px] overflow-hidden">
              {post.attached_media_url && (
                <img src={post.attached_media_url} alt="" className="w-full max-h-96 object-cover" />
              )}
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                  {post.ai_generated && <span className="px-2 py-0.5 rounded-full bg-[#3A7A8C] text-white text-[10px] font-semibold">AI</span>}
                  <span className="flex items-center gap-1 text-[#a16207] text-xs"><Clock size={12} /> {new Date(post.scheduled_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </div>
                <p className="text-[#3C4048] text-sm whitespace-pre-line">{post.draft_text}</p>

                <div className="flex items-center gap-1.5 text-[#3A7A8C] text-sm font-semibold pt-1 border-t border-[#eef1f2]">
                  <ChatCircleDots size={16} /> Your feedback
                  {hasUnread(post) && <span className="w-2 h-2 rounded-full bg-[#ef4444]" data-testid={`portal-unread-${post.id}`} />}
                </div>
                <CommentsThread api={api} postId={post.id} currentName={user?.fullName || user?.firstName || "Staff"} />
              </div>
            </div>
          ))
        )}
      </main>
      )}
    </div>
  );
}
